import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Home as View } from '@views';
import { setActivated } from '@store/user/duck';
import { getBookedRooms } from '@store/booking/duck';
import { getEventsCategories } from '@store/event/duck';
import { getAnnouncements } from '@store/announcement/duck';
import { getCompanyOnboarding } from '@store/company/duck';
import { getReferFriendOptions, getMetadata } from '@store/metadata/duck';
import { LOADING, SUCCESS } from '@constants/requestPhase';
import { HEADER_ANNOUNCEMENT_KEY, ONBOARDING_BANNER_KEY } from '@constants/common';
import { storage } from '@utils';

const Home = () => {
  const { isActivated, user } = useSelector(store => store.userStore);

  const headerAnnouncement = storage.getFromLocalStorage(HEADER_ANNOUNCEMENT_KEY);

  const onboardingBannerClosedAt = storage.getFromLocalStorage(ONBOARDING_BANNER_KEY);

  const parsedHeaderAnnouncement = headerAnnouncement
    ? JSON.parse(headerAnnouncement)
    : {
        id: null,
        locationId: null,
      };

  const { currentLocation, getCurrentLocationPhase } = useSelector(store => store.locationStore);

  const { announcements } = useSelector(store => store.announcementsStore);

  const { getEventsCategoriesPhase } = useSelector(store => store.eventStore);

  const { getMeetingRoomsPhase } = useSelector(store => store.roomStore);

  const { getReferFriendPhase, referFriend, metadata, getMetadataPhase } = useSelector(store => store.metadataStore);

  const { user: currentUser } = useSelector(store => store.userStore);

  const { isWhiteLabeled } = currentLocation;

  const [isWhiteLabelingOpen, setIsWhiteLabelingOpen] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const pageTitle = 'Mindspace: Home';
    document.title = pageTitle;

    window.gtag('event', 'page_view', {
      page_title: pageTitle,
    });

    dispatch(getBookedRooms());
    dispatch(getReferFriendOptions());
  }, []);

  useEffect(() => {
    if (currentUser?.isOnboardingAvailable) {
      dispatch(getCompanyOnboarding());
    }
  }, [currentUser?.isOnboardingAvailable]);

  useEffect(() => {
    if (getCurrentLocationPhase === SUCCESS && parsedHeaderAnnouncement.locationId !== currentLocation?.id) {
      dispatch(getAnnouncements(currentLocation?.id));
    }
  }, [getCurrentLocationPhase]);

  useEffect(() => {
    if (!Object.keys(metadata).length) {
      dispatch(getMetadata());
    }
    if (Object.keys(currentLocation).length && !currentLocation.exploreLocation) {
      dispatch(getEventsCategories());
    }
  }, [currentLocation]);

  useEffect(() => {
    setIsWhiteLabelingOpen(isActivated && isWhiteLabeled && location?.state?.from === 'login');
  }, [isWhiteLabeled]);

  useEffect(() => {
    if (isWhiteLabelingOpen) {
      setTimeout(() => {
        setIsWhiteLabelingOpen(false);
        dispatch(setActivated(false));
      }, 3500);
    }
  }, [isWhiteLabelingOpen]);

  const locationTeam = useMemo(
    () =>
      currentLocation.communityManagers?.map(({ id, firstName, lastName, role, jobTitle, imageUrl }) => ({
        id,
        firstName,
        lastName,
        role,
        jobTitle,
        imageUrl,
      })),
    [currentLocation.id],
  );

  return (
    <View
      isOnboardingAvailable={currentUser?.isOnboardingAvailable}
      onboardingBannerClosedAt={onboardingBannerClosedAt}
      isLoading={
        getEventsCategoriesPhase === LOADING ||
        getMeetingRoomsPhase === LOADING ||
        getCurrentLocationPhase === LOADING ||
        getReferFriendPhase === LOADING ||
        getMetadataPhase === LOADING
      }
      firstName={user.firstName}
      location={currentLocation.title}
      upcomingEvent={currentLocation.upcomingEvent}
      locationTeam={locationTeam}
      locationLongitude={currentLocation.longtitude}
      locationLatitude={currentLocation.latitude}
      locationAddress={currentLocation.address}
      locationImage={currentLocation.image}
      wifiSettings={currentLocation.wifiSettings}
      locationLogo={currentLocation.logo}
      isWhiteLabelingOpen={isWhiteLabelingOpen}
      announcement={announcements?.announcement}
      headerAnnouncement={announcements?.heads_up}
      parsedHeaderAnnouncementSavedLocally={parsedHeaderAnnouncement}
      isDiscountAvailable={user.shouldDisplayDiscountModal}
      referFriendCurrency={referFriend.reward?.currency}
      referFriendAmount={referFriend.reward?.amount}
    />
  );
};

export default Home;
